<script setup lang="ts">
import { Order, PaginationData, Statuses } from '@/@types/order'
import FormFilters from '@/components/shared/base_orders/FormFilters.vue'
import Pagination from '@/components/shared/base_orders/Pagination.vue'
import Table from '@/components/shared/base_orders/Table.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { computed, provide } from 'vue'

const props = defineProps<{
    orders: Order[]
    old_orders: Order[]
    current_workshop_titles: string[]
    statuses: Statuses
    id: number
    status: string
    current_pagination: PaginationData
    old_pagination: PaginationData
}>()

const params = useUrlSearchParams()
const hasAnyOrders = computed(() => props.orders.length > 0 || props.old_orders.length > 0)
const currentWorkshopHeading = computed(() => {
    if (!props.current_workshop_titles.length) return $t('orders.current_workshops')
    return props.current_workshop_titles.join(' | ')
})

provide('params', params)
</script>

<template>
    <Head :title="$t('orders.title')"></Head>

    <LightLayout contentClasses="bg-slate-50">
        <div class="container flex flex-col gap-6">
            <Title>
                {{ $t('orders.title') }}
            </Title>

            <Card :data-blank="!hasAnyOrders && !id && !status" class="flex min-h-96 flex-col gap-8 border-none data-[blank=true]:justify-center">
                <CardHeader v-if="hasAnyOrders || id || status" class="pb-0">
                    <FormFilters :status="status" :id="id" :statuses="statuses" />
                </CardHeader>
                <CardContent>
                    <div v-if="hasAnyOrders" class="flex flex-col gap-8">
                        <section v-if="orders.length" class="flex flex-col gap-4">
                            <h2 class="text-lg font-semibold text-slate-900">
                                {{ currentWorkshopHeading }}
                            </h2>
                            <Table
                                :orders="orders"
                                :only-props="['orders', 'old_orders', 'current_pagination', 'old_pagination', 'status', 'id', 'current_workshop_titles']"
                            />
                            <CardFooter v-if="current_pagination.per_page < current_pagination.total" class="px-0 pb-0">
                                <Pagination
                                    :pagination="current_pagination"
                                    page-param="current_page"
                                    :only-props="['orders', 'current_pagination', 'status', 'id', 'current_workshop_titles']"
                                />
                            </CardFooter>
                        </section>

                        <section v-if="old_orders.length" class="flex flex-col gap-4">
                            <h2 class="text-lg font-semibold text-slate-900">
                                {{ $t('orders.past_forums') }}
                            </h2>
                            <Table
                                :orders="old_orders"
                                :only-props="['orders', 'old_orders', 'current_pagination', 'old_pagination', 'status', 'id', 'current_workshop_titles']"
                            />
                            <CardFooter v-if="old_pagination.per_page < old_pagination.total" class="px-0 pb-0">
                                <Pagination
                                    :pagination="old_pagination"
                                    page-param="old_page"
                                    :only-props="['old_orders', 'old_pagination', 'status', 'id']"
                                />
                            </CardFooter>
                        </section>
                    </div>

                    <div v-else>
                        <div v-if="status || id" class="flex flex-col items-center gap-6">
                            <p>{{ $t('No orders were found with the applied filters') }}.</p>
                            <Button variant="secondary" @click.prevent="$inertia.visit('/orders')">
                                {{ $t('Clear filters') }}
                            </Button>
                        </div>
                        <div v-else class="flex flex-col items-center gap-6">
                            <p>{{ $t('There are no orders registered on the platform') }}.</p>
                            <Button @click="$inertia.visit('/')">
                                {{ $t('Keep shopping') }}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    </LightLayout>
</template>
